import { Box, TextField } from '@mui/material';
import { RiArrowUpCircleFill, RiAttachment2 } from '@remixicon/react';
import { useCallback, useEffect, useState } from 'react';
import { AgButton } from 'src/components/ui';
import { uploadToFilestack } from 'src/lib/filestack';
import useAgentSendMessage from '../hooks/useAgentSendMessage';
import { useAiChatStore } from '../store/aiChat.store';
import AiChatFileAttachment from './AiChatFileAttachment';
import AiChatVoiceRecognition from './AiChatVoiceRecognition';

export default function AiChatMessageInput() {
  const sendMessage = useAgentSendMessage();
  const messageInput = useAiChatStore((state) => state.getMessageParts());
  const setMessageInput = useAiChatStore((state) => state.setMessageInput);
  const attachedFiles = useAiChatStore((state) => state.attachedFiles);
  const setAttachedFiles = useAiChatStore((state) => state.setAttachedFiles);
  const addAttachedFiles = useAiChatStore((state) => state.addAttachedFiles);
  const updateAttachedFile = useAiChatStore((state) => state.updateAttachedFile);
  const handleRemoveFile = useAiChatStore((state) => state.removeAttachedFile);
  const setWaitingForResponse = useAiChatStore((state) => state.setWaitingForResponse);
  const waitingForResponse = useAiChatStore((state) => state.waitingForResponse);
  const isVoiceRecording = useAiChatStore((state) => state.isVoiceRecording);

  // Upload files immediately when they're added
  useEffect(() => {
    const uploadFiles = async () => {
      // Get files that need uploading (have file object, are uploading, and no url yet)
      const filesToUpload = attachedFiles.filter((f) => f.file && f.uploading && !f.url);

      for (const attachedFile of filesToUpload) {
        try {
          const result = await uploadToFilestack(attachedFile.file!, attachedFile.file!.name);

          updateAttachedFile(attachedFile.id, { uploading: false, url: result.url, filename: attachedFile.file!.name });
        } catch (error) {
          console.error('Error uploading file:', error);
          updateAttachedFile(attachedFile.id, { uploading: false, error: 'Upload failed' });
        }
      }
    };

    uploadFiles();
  }, [attachedFiles, updateAttachedFile]);

  const handleSendMessage = useCallback(
    async (message: string) => {
      if (!message.trim() || waitingForResponse) {
        return;
      }

      // Check if any files are still uploading
      const stillUploading = attachedFiles.some((f) => f.uploading);
      if (stillUploading) {
        console.log('Still uploading files, please wait...');
        return;
      }

      setWaitingForResponse(true);

      try {
        // Get document URLs from already uploaded files
        const documentUrls = attachedFiles
          .filter((f) => f.url && !f.error)
          .map((f) => ({ url: f.url!, filename: f.filename || f.file?.name || 'document' }));

        // Send message with document URLs
        sendMessage(message, documentUrls);
        setMessageInput('');
        setAttachedFiles([]);
      } catch (error) {
        console.error('Error sending message:', error);
        setWaitingForResponse(false);
      }
    },
    [attachedFiles, sendMessage, setWaitingForResponse, waitingForResponse, setMessageInput, setAttachedFiles]
  );

  const [inputFocused, setInputFocused] = useState(false);

  return (
    <>
      <TextField
        minRows={3}
        multiline
        variant="outlined"
        placeholder={waitingForResponse ? 'Waiting for response...' : 'Type your prompt here or drag and drop files'}
        maxRows={20}
        value={messageInput}
        onChange={(e) => setMessageInput(e.target.value)}
        disabled={waitingForResponse || isVoiceRecording}
        onFocus={() => setInputFocused(true)}
        onBlur={() => setInputFocused(false)}
        sx={{
          mx: '24px',
          mt: '16px',
          '.MuiInputBase-root': {
            flexDirection: 'column', // make the end adornment more of a bottom adornment
          },
        }}
        slotProps={{
          input: {
            startAdornment: (
              <Box sx={{ display: 'flex', width: '100%', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                {attachedFiles.map((attachedFile) => (
                  <AiChatFileAttachment
                    key={attachedFile.id}
                    attachedFile={attachedFile}
                    onRemove={() => handleRemoveFile(attachedFile.id)}
                  />
                ))}
              </Box>
            ),
            endAdornment: (
              <Box
                sx={{
                  display: 'flex',
                  width: 'calc(100% + 16px)',
                  justifyContent: 'space-between',
                  marginBottom: '-12px',
                }}
              >
                <Box>
                  <AgButton
                    variant="ghost"
                    size="icon-sm"
                    shape="circle"
                    isDisabled={waitingForResponse || isVoiceRecording}
                    onPress={() => {
                      const fileInput = document.getElementById('ai-chat-file-input') as HTMLInputElement;
                      fileInput?.click();
                    }}
                  >
                    <RiAttachment2 size={22} />
                  </AgButton>
                  <input
                    id="ai-chat-file-input"
                    type="file"
                    hidden
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.tiff,.gif,.bmp,.xlsx,.xls,.csv,.mp3,.mp4,.m4a,.wav,.flac,.ogg,.webm"
                    onChange={(e) => {
                      if (e.target.files) {
                        addAttachedFiles(Array.from(e.target.files));
                      }
                      e.target.value = '';
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', alignContent: 'center' }}>
                  <AiChatVoiceRecognition enablePushToTalk={!inputFocused} />
                  <AgButton
                    variant="ghost"
                    size="icon-sm"
                    shape="circle"
                    onPress={() => {
                      handleSendMessage(messageInput);
                    }}
                    isDisabled={waitingForResponse || messageInput.length === 0}
                  >
                    <RiArrowUpCircleFill
                      size={24}
                      className={messageInput.length > 0 ? 'text-primary' : 'text-[#BEBEBC]'}
                    />
                  </AgButton>
                </Box>
              </Box>
            ),
            onKeyDown: (e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(messageInput);
              }
            },
          },
        }}
      />
    </>
  );
}
